/**
 * TravelItinerarySection — day-by-day timeline block.
 *
 * Extracted from TravelListView so the summary view can embed the same
 * rendering without duplicating the day/transit row components. This is
 * the read-only "what's the plan, day by day" stretch of the page.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type {
  TravelAttachmentResponse,
  TravelEventResponse,
  TravelItemResponse,
  TravelTripResponse,
} from '../api';
import {
  EVENT_TYPE_LABELS,
  TRANSIT_MODE_EMOJI,
  formatDayDate,
  getCityColor,
  getEventTypeColor,
  resetCityColors,
} from './TravelHelpers';
import { TravelAttachmentPreview } from './TravelAttachmentPreview';
import { formatTime } from './TravelSettings';

interface TravelItinerarySectionProps {
  trip: TravelTripResponse;
  items: TravelItemResponse[];
  events: TravelEventResponse[];
  attachments?: TravelAttachmentResponse[];
}

export function TravelItinerarySection({
  trip,
  items,
  events,
  attachments = [],
}: TravelItinerarySectionProps) {
  resetCityColors();

  const eventsByItem = new Map<string, TravelEventResponse[]>();
  for (const event of events) {
    const list = eventsByItem.get(event.itemId) || [];
    list.push(event);
    eventsByItem.set(event.itemId, list);
  }

  const attachmentsByEvent = new Map<string, TravelAttachmentResponse[]>();
  for (const att of attachments) {
    if (!att.eventId) continue;
    const list = attachmentsByEvent.get(att.eventId) || [];
    list.push(att);
    attachmentsByEvent.set(att.eventId, list);
  }

  return (
    <div className="space-y-0">
      {items.map((item, idx) => {
        if (item.itemType === 'transit') {
          return <TransitBlock key={item.itemId} item={item} />;
        }
        const dayEvents = eventsByItem.get(item.itemId) || [];
        const isFirst = idx === 0;
        return (
          <DayBlock
            key={item.itemId}
            trip={trip}
            item={item}
            events={dayEvents}
            attachmentsByEvent={attachmentsByEvent}
            isFirst={isFirst}
          />
        );
      })}
    </div>
  );
}

function DayBlock({
  trip,
  item,
  events,
  attachmentsByEvent,
  isFirst,
}: {
  trip: TravelTripResponse;
  item: TravelItemResponse;
  events: TravelEventResponse[];
  attachmentsByEvent: Map<string, TravelAttachmentResponse[]>;
  isFirst: boolean;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const cityColor = item.city ? getCityColor(item.city) : '#94a3b8';
  const dateStr = formatDayDate(trip.startDate, item.dayNumber ?? 1);

  const allLocations = [
    item.city && { city: item.city, country: item.country },
    ...(item.locations || []),
  ].filter(Boolean) as Array<{ city: string; country?: string | null }>;

  const Chevron = collapsed ? ChevronRight : ChevronDown;

  return (
    <div className={`${isFirst ? '' : 'mt-10'}`}>
      <div className="flex items-end gap-4 pb-3 border-b-2 border-foreground/10">
        <div
          className="w-1.5 self-stretch rounded-full shrink-0"
          style={{ backgroundColor: cityColor }}
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#2563eb] dark:text-[#38bdf8]">{dateStr}</div>
          <h2 className="text-2xl font-bold tracking-tight leading-tight mt-0.5">
            {item.dayTitle || `Day ${item.dayNumber}`}
          </h2>
          {allLocations.length > 0 && (
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
              {allLocations.map((loc, i) => (
                <span key={i} className="text-sm text-muted-foreground">
                  {loc.city}
                  {loc.country ? `, ${loc.country}` : ''}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="shrink-0 p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          aria-label={collapsed ? 'Expand day' : 'Collapse day'}
        >
          <Chevron className="h-5 w-5" />
        </button>
      </div>

      {!collapsed && (
        <>
          {item.accommodationName && (
            <div className="mt-3 ml-5 flex items-start gap-2 text-sm">
              <span className="text-base leading-5">{'🏨'}</span>
              <div>
                <span className="font-medium">{item.accommodationName}</span>
                {item.accommodationAddress && (
                  <span className="text-muted-foreground ml-1">— {item.accommodationAddress}</span>
                )}
                <div className="text-xs text-muted-foreground mt-0.5">
                  {item.accommodationCheckin && <span>Check-in: {item.accommodationCheckin}</span>}
                  {item.accommodationCheckin && item.accommodationCheckout && (
                    <span className="mx-1.5">·</span>
                  )}
                  {item.accommodationCheckout && (
                    <span>Check-out: {item.accommodationCheckout}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {events.length > 0 ? (
            <div className="mt-3 ml-5 space-y-1">
              {events.map((ev) => (
                <EventRow
                  key={ev.eventId}
                  event={ev}
                  attachments={attachmentsByEvent.get(ev.eventId) || []}
                />
              ))}
            </div>
          ) : (
            <div className="mt-3 ml-5 text-sm text-muted-foreground/50 italic">
              No events planned
            </div>
          )}
        </>
      )}

      {collapsed && events.length > 0 && (
        <div className="mt-2 ml-5 text-xs text-muted-foreground">
          {events.length} {events.length === 1 ? 'event' : 'events'}
        </div>
      )}
    </div>
  );
}

function EventRow({
  event,
  attachments,
}: {
  event: TravelEventResponse;
  attachments: TravelAttachmentResponse[];
}) {
  const color = getEventTypeColor(event.eventType);

  return (
    <div className="flex items-start gap-3 py-1.5">
      <div className="w-16 shrink-0 text-right">
        {event.time ? (
          <span className="text-sm font-mono text-muted-foreground">{formatTime(event.time)}</span>
        ) : (
          <span className="text-sm text-muted-foreground/30">—</span>
        )}
      </div>

      <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: color }} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{event.title}</span>
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0"
            style={{ backgroundColor: color + '18', color }}
          >
            {EVENT_TYPE_LABELS[event.eventType]}
          </span>
        </div>
        {event.notes && <p className="text-xs text-muted-foreground mt-0.5">{event.notes}</p>}
        <TravelAttachmentPreview attachments={attachments} />
      </div>
    </div>
  );
}

function TransitBlock({ item }: { item: TravelItemResponse }) {
  return (
    <div className="my-6 flex items-center gap-3 px-5">
      <div className="flex-1 h-px border-t border-dashed border-muted-foreground/30" />
      <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full">
        {item.transitMode && <span>{TRANSIT_MODE_EMOJI[item.transitMode]}</span>}
        <span className="font-medium">
          {item.transitFrom || '?'} → {item.transitTo || '?'}
        </span>
        {(item.transitDeparture || item.transitArrival) && (
          <span className="text-xs text-muted-foreground/70">
            {item.transitDeparture && item.transitArrival
              ? `${item.transitDeparture} – ${item.transitArrival}`
              : item.transitDeparture || item.transitArrival}
          </span>
        )}
      </div>
      <div className="flex-1 h-px border-t border-dashed border-muted-foreground/30" />
    </div>
  );
}
